import React, { useState, useEffect } from "react";
import PasswordIcon from "../assets/images/login-flow/password.svg";
import { Link } from "react-router-dom";
import { Form, Button, Col, Row } from "react-bootstrap";
import "../scss/login.scss";

import { useDispatch, useSelector } from "react-redux";
import {
  verifyEmail,
  resetPassword,
  clearMessages,
} from "../features/auth/authActions";
import { getPageContent } from "../features/visitor/visitorActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify"; // Import toast
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function ChangePassword() {
  const [headerData, setHeaderData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [emailToken, setEmailToken] = useState("");
  const [oldEmail, setOldEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [isTokenVerified, setIsTokenVerified] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();

  // Extract the page name from the pathname
  const pageName = location.pathname.split("/")[1]; // This will get "verify-email"

  const { loading, successMessage, errorMessage } = useSelector(
    (state) => state.auth
  );
  const { pageResponse } = useSelector((state) => state.visitor);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    const newEmailAddress = params.get("new-email");
    const oldEmailAddress = params.get("old-email");

    setEmailToken(tokenParam);
    setNewEmail(newEmailAddress);
    setOldEmail(oldEmailAddress);
  }, [location.search]);

  useEffect(() => {
    if (emailToken && !isTokenVerified) {
      let formData = {
        token: emailToken,
        old_email: oldEmail,
        new_email: newEmail,
      };
      dispatch(verifyEmail(formData));
    }
  }, [emailToken, newEmail, oldEmail, dispatch, isTokenVerified]);

  useEffect(() => {
    if (successMessage && isChangePassword) {
      toast.success(successMessage, { autoClose: 3000, hideProgressBar: true });
      setPassword("");
      setConfirmPassword("");
      setConfirmPassword(false);
      setIsTokenVerified(false);
      setTimeout(() => (window.location.href = "/login"), 3000);
      dispatch(clearMessages());
    }

    if (successMessage && pageName === "verify-email") {
      toast.success(successMessage, { autoClose: 3000, hideProgressBar: true });
      dispatch(clearMessages());
      setTimeout(() => (window.location.href = "/login"), 3000);
    }

    if (errorMessage) {
      toast.error(errorMessage, { autoClose: 3000, hideProgressBar: true });
      dispatch(clearMessages());
    }
  }, [successMessage, errorMessage, isChangePassword, pageName, dispatch]);

  const validate = () => {
    const errors = {};

    // Check for empty fields
    if (!password) errors.password = "Password is required.";
    if (password !== confirmPassword)
      errors.confirmPassword = "Passwords must match.";

    // Check password length
    if (password && password.length < 8)
      errors.password = "Password must be at least 8 characters long.";

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Initial blank errors
    setErrors({});

    // Validate form inputs
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Dismiss any currently visible toasts
    toast.dismiss();

    // set is change password to true
    setIsChangePassword(true);

    // Dispatch reset password action
    let data = {
      token: emailToken,
      password: password,
    };
    dispatch(resetPassword(data));
  };

  useEffect(() => {
    dispatch(getPageContent());
  }, [dispatch]);

  useEffect(() => {
    if (pageResponse) {
      let footer = pageResponse.find((r) => r.code === "footer");
      setHeaderData(footer.footer_section);
    }
  }, [pageResponse]);

  return (
    <>
      <Helmet>
        <title>Change Password | Helpotani</title>
        <meta
          name="description"
          content="Update your password to maintain the security of your Helpotani account. Follow the steps to create a new password."
        />
        <meta
          name="keywords"
          content="Change Password, Security, Account, Helpotani, Update Password"
        />
      </Helmet>

      <section className="login">
        <div className="login-box">
          <Link to="/" className="logo-img">
            <img
              src={`${process.env.REACT_APP_PHP_BACKEND_MEDIA_URL}${headerData?.main_logo}`}
              alt=""
            />
          </Link>
          {pageName !== "verify-email" ? (
            <div className="w-100">
              <h1 className="text-center">Update New Password</h1>
              <p className="text-center">
                Please set a new password for smooth sign in process.
              </p>
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="formPassword" className="form-group">
                  <Form.Label>Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                    <img src={PasswordIcon} alt="Password" />
                  </div>
                </Form.Group>

                <Form.Group
                  controlId="formConfirmPassword"
                  className="form-group"
                >
                  <Form.Label>Confirm Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      isInvalid={!!errors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                    <img src={PasswordIcon} alt="Confirm Password" />
                  </div>
                </Form.Group>

                <div className="text-center">
                  <div className="button-group mt-5">
                    <Button
                      disabled={loading}
                      className="custom-bttn"
                      type="submit"
                    >
                      {loading ? (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          size="lg"
                          className="spinner-icon"
                        />
                      ) : (
                        "Update Password"
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          ) : (
            <div className="sign-box-wrap">
              <Row>
                <Col lg="12" className="text-center">
                  <h1 className="text-center">Verifying Email Address...</h1>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
export default ChangePassword;
